import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { BRAND } from "@/config/brand";
import { TESTIDS } from "@/constants/testIds";

const NAV = [
  { label: "Home", href: "#top", id: TESTIDS.header.navHome },
  { label: "Services", href: "#services", id: TESTIDS.header.navServices },
  { label: "Gallery", href: "#gallery", id: TESTIDS.header.navGallery },
  { label: "About", href: "#about", id: TESTIDS.header.navAbout },
  { label: "Contact", href: "#contact", id: TESTIDS.header.navContact },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-black/80 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-20 flex items-center justify-between gap-6">
        <Link
          to="/"
          data-testid={TESTIDS.header.logo}
          className="flex items-center gap-3 shrink-0"
        >
          <img
            src={BRAND.logo}
            alt="Vasquez & Son Construction Inc."
            className="h-11 w-11 md:h-12 md:w-12 object-contain"
          />
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="font-display uppercase tracking-tight text-white text-[15px] md:text-base font-semibold">
              Vasquez & Son
            </span>
            <span className="font-mono-accent text-[10px] tracking-[0.25em] text-gray-400 uppercase">
              Construction Inc.
            </span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((n) => (
            <a
              key={n.label}
              href={n.href}
              data-testid={n.id}
              className="font-display uppercase tracking-[0.15em] text-sm text-gray-300 hover:text-white transition-colors relative group"
            >
              {n.label}
              <span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-[color:var(--vs-blue)] transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <a
          href={`tel:${BRAND.phoneRaw}`}
          data-testid={TESTIDS.header.phoneCta}
          className="hidden md:inline-flex items-center gap-2 border-2 border-white/25 hover:border-[color:var(--vs-blue)] px-4 py-2.5 font-display uppercase tracking-[0.15em] text-sm text-white transition-colors"
        >
          <Phone className="w-4 h-4 text-[color:var(--vs-blue)]" />
          {BRAND.phone}
        </a>

        <button
          onClick={() => setOpen((v) => !v)}
          data-testid={TESTIDS.header.mobileToggle}
          className="lg:hidden text-white p-2"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div
          data-testid={TESTIDS.header.mobilePanel}
          className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-white/10"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV.map((n) => (
              <a
                key={n.label}
                href={n.href}
                onClick={() => setOpen(false)}
                className="font-display uppercase tracking-[0.15em] text-lg text-white/90 hover:text-[color:var(--vs-blue)]"
              >
                {n.label}
              </a>
            ))}
            <a
              href={`tel:${BRAND.phoneRaw}`}
              className="btn-vs btn-primary mt-2 justify-center"
            >
              <Phone className="w-4 h-4" />
              {BRAND.phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
